import { MessageTemplateLanguage } from './message-template.model';

export type LocalizedText = Record<MessageTemplateLanguage, string>;

export interface TranslatedVariableOption {
	value: LocalizedText;
	/** Shown in the UI dropdown. Defaults to `value` if not provided. */
	label?: LocalizedText;
}

// ---------------------------------------------------------------------------
// 5-kind taxonomy
// ---------------------------------------------------------------------------

interface TemplateVariableBase {
	name: string;
	/** Localized short label shown as form field label in the UI. */
	shortLabel?: LocalizedText;
	/** Localized description shown as tooltip in the UI. */
	label?: LocalizedText;
}

/** Non-built-in variable types that can be stored in a CMS block. */
export type UserDefinedTemplateVariable =
	| SimpleStaticVariable
	| SimpleDynamicVariable
	| TranslatedDynamicVariable;

/**
 * Translated default value, no UI override field.
 * Examples: `senderName`, `guidelineLink`
 */
export interface SimpleStaticVariable extends TemplateVariableBase {
	kind: 'simple-static';
	builtIn: false;
	defaultValue: LocalizedText;
}

/**
 * Value resolved from runtime data, no UI override field.
 * Examples: `patientId`, `patientName`, `customerId`, `customerName`, `productName`
 */
export interface BoundStaticVariable extends TemplateVariableBase {
	kind: 'bound-static';
	builtIn: true;
}

/**
 * Plain string options (not translated) with a selection/autocomplete field.
 * The first option is always the default.
 * Examples: `toothRange`, `toothPair`
 */
export interface SimpleDynamicVariable extends TemplateVariableBase {
	kind: 'simple-dynamic';
	builtIn: false;
	options: string[];
}

/**
 * Value resolved from runtime data with a UI field for override.
 * Examples: `deadline`, `productAlternativeName`, `webviewerLink-1`, `webviewerLink-2`
 */
export interface BoundDynamicVariable extends TemplateVariableBase {
	kind: 'bound-dynamic';
	builtIn: true;
}

/**
 * Translated option values with a selection/autocomplete field.
 * The first option is always the default.
 * Examples: `obstacleType`, `findingSummary`
 */
export interface TranslatedDynamicVariable extends TemplateVariableBase {
	kind: 'translated-dynamic';
	builtIn: false;
	options: TranslatedVariableOption[];
}

// ---------------------------------------------------------------------------
// Union types
// ---------------------------------------------------------------------------

export type TemplateVariable =
	| SimpleStaticVariable
	| BoundStaticVariable
	| SimpleDynamicVariable
	| BoundDynamicVariable
	| TranslatedDynamicVariable;

export type StaticVariable = SimpleStaticVariable | BoundStaticVariable;
export type DynamicVariable = SimpleDynamicVariable | BoundDynamicVariable | TranslatedDynamicVariable;
export type VariableWithOptions = SimpleDynamicVariable | TranslatedDynamicVariable;

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isStaticVariable(v: TemplateVariable): v is StaticVariable {
	return v.kind === 'simple-static' || v.kind === 'bound-static';
}

export function isDynamicVariable(v: TemplateVariable): v is DynamicVariable {
	return v.kind === 'simple-dynamic' || v.kind === 'bound-dynamic' || v.kind === 'translated-dynamic';
}

export function hasOptions(v: TemplateVariable): v is VariableWithOptions {
	return v.kind === 'simple-dynamic' || v.kind === 'translated-dynamic';
}

export function isTranslatedDynamic(v: TemplateVariable): v is TranslatedDynamicVariable {
	return v.kind === 'translated-dynamic';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getDefaultValue(variable: VariableWithOptions, lang: MessageTemplateLanguage): string {
	if (variable.kind === 'translated-dynamic') {
		return variable.options[0]?.value[lang] ?? '';
	}
	return variable.options[0] ?? '';
}

export function getOptionLabels(variable: VariableWithOptions, lang: MessageTemplateLanguage): string[] {
	if (variable.kind === 'translated-dynamic') {
		return variable.options.map(o => (o.label ?? o.value)[lang]);
	}
	return [...variable.options];
}

/**
 * When switching language, find the matching option by old-language value
 * and return the new-language value. Falls back to the default.
 */
export function switchLanguageValue(
	variable: TranslatedDynamicVariable,
	currentValue: string,
	prevLang: MessageTemplateLanguage,
	newLang: MessageTemplateLanguage,
): string {
	const match = variable.options.find(o => o.value[prevLang] === currentValue);
	return match ? match.value[newLang] : (variable.options[0]?.value[newLang] ?? '');
}
